import React from 'react'

import { routes } from '@sketch/modules-common'

// Components
import { Button, LinkButton } from '@sketch/components'

// Local Components
import BillingInfoTable from '../BillingInfoTable'
import { EditButton } from './EditButton'

// Utils
import { formatPriceShort, dateFormat } from '@sketch/utils'
import { PLAN_TYPE_LABEL } from 'modules/workspace/utils'

// GQL
import {
  BillingPlanFragment,
  BillingSeatsInfoFragment,
  PaymentDetailsFragment,
} from '@sketch/gql-types'

interface CurrentPlanProps {
  workspaceId: string
  customerId: string
  isWorkspaceOnTrial: boolean
  customerHasPaymentDetails: boolean
  currentPlan?: BillingPlanFragment
  trialEndDate: string
  isSubscriptionEnd: boolean
  isPartner?: boolean
  isNewPricingEnabled: boolean
  seats: BillingSeatsInfoFragment
  renewalDate: string
  paymentDetails?: PaymentDetailsFragment
  onWorkspaceSettingRefresh: () => Promise<void>
  onReactivateSubscription: () => void
}

const Header: React.FC = () => (
  <BillingInfoTable.HeadingCell>Current Plan</BillingInfoTable.HeadingCell>
)

export const CurrentPlan = ({
  workspaceId,
  customerId,
  isWorkspaceOnTrial,
  customerHasPaymentDetails,
  currentPlan,
  trialEndDate,
  isSubscriptionEnd,
  isPartner,
  seats,
  renewalDate,
  paymentDetails,
  onWorkspaceSettingRefresh,
  onReactivateSubscription,
}: CurrentPlanProps) => {
  /**
   * The workspace is on trial and the customer already configured a following
   * plan
   */
  if (isWorkspaceOnTrial && currentPlan && customerHasPaymentDetails) {
    return (
      <BillingInfoTable.Row>
        <Header />
        <BillingInfoTable.Cell>
          <div>
            <strong>Trial</strong>
            <br />
            Ends on {dateFormat(new Date(trialEndDate))}
          </div>
        </BillingInfoTable.Cell>
        {isSubscriptionEnd && (
          <BillingInfoTable.Cell>
            <Button
              variant="primary"
              size="32"
              onClick={onReactivateSubscription}
            >
              Subscribe Again
            </Button>
          </BillingInfoTable.Cell>
        )}
        {!isSubscriptionEnd && !isPartner && (
          <EditButton
            customerId={customerId}
            isWorkspaceOnTrial={isWorkspaceOnTrial}
            currentPlan={currentPlan}
            seats={seats}
            paymentDetails={paymentDetails}
            onWorkspaceSettingRefresh={onWorkspaceSettingRefresh}
          />
        )}
      </BillingInfoTable.Row>
    )
  }

  if (isWorkspaceOnTrial) {
    /**
     * The workspace is only on trial. After that, the workspace will be
     * de-activated
     */
    return (
      <BillingInfoTable.Row>
        <Header />
        <BillingInfoTable.Cell>
          <div>
            <strong>Trial</strong>
            <br />
            Ends on {dateFormat(new Date(trialEndDate))}
          </div>
        </BillingInfoTable.Cell>
        <BillingInfoTable.Cell>
          <LinkButton
            data-testid="setup-subscription-button"
            variant="primary"
            size="32"
            to={routes.WORKSPACE_SUBSCRIBE.create({
              workspaceId,
            })}
          >
            Set Up a Subscription
          </LinkButton>
        </BillingInfoTable.Cell>
      </BillingInfoTable.Row>
    )
  }
  /**
   * The workspace is definitely not on trial
   */
  const isEducationPlan = currentPlan?.product === 'STUDENT'

  return (
    <BillingInfoTable.Row>
      <Header />

      <BillingInfoTable.Cell>
        {!isEducationPlan ? (
          <div>
            <strong data-testid="plan-name">
              {PLAN_TYPE_LABEL[currentPlan!.type]}
            </strong>
            <br />
            <span data-testid="plan-price-per-editor">
              <strong>{formatPriceShort(currentPlan!.invoicePrice)}</strong> per
              Seat, per {currentPlan?.type === 'YEARLY' ? 'year' : 'month'}
            </span>
          </div>
        ) : (
          <div>
            <strong data-testid="plan-name">Education </strong>
            <br />
            <span data-testid="plan-price-per-editor">
              Ends on {dateFormat(new Date(renewalDate))}
            </span>
          </div>
        )}
      </BillingInfoTable.Cell>
      {isSubscriptionEnd && (
        <BillingInfoTable.Cell>
          <Button
            variant="primary"
            size="32"
            onClick={onReactivateSubscription}
          >
            {isPartner ? 'Reactivate' : 'Subscribe Again'}
          </Button>
        </BillingInfoTable.Cell>
      )}
      {!isSubscriptionEnd && !isPartner && (
        <EditButton
          customerId={customerId}
          isWorkspaceOnTrial={isWorkspaceOnTrial}
          currentPlan={currentPlan}
          seats={seats}
          paymentDetails={paymentDetails}
          onWorkspaceSettingRefresh={onWorkspaceSettingRefresh}
        />
      )}
    </BillingInfoTable.Row>
  )
}
